import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { NodeCard } from '@/modules/folders/components/NodeCard';
import type { NodeAction } from '@/modules/folders/components/NodeMenu';
import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import { foldersApi } from '@/shared/folders';
import type { FoldersNode } from '@/shared/folders';
import { HomeCardGrid, HomeSection } from './HomeSection';
import { useModuleMatches } from './HomeSearch';

interface Props {
  term: string;
  orgId: string | null;
  starredIds: ReadonlySet<string>;
  onToggleStar: (node: FoldersNode) => void;
  actionsFor: (node: FoldersNode) => readonly NodeAction[];
}

/**
 * What the Home sections are replaced by once a search is submitted.
 *
 * Grouped rather than ranked: "apps", "folders" and "files" are different
 * kinds of answer, and interleaving them by relevance would make a module and
 * a spreadsheet compete for the same slot.
 */
export function HomeSearchResults({ term, orgId, starredIds, onToggleStar, actionsFor }: Props) {
  const { t } = useI18n();
  const [nodes, setNodes] = useState<readonly FoldersNode[] | null>(null);
  const [failed, setFailed] = useState(false);
  const apps = useModuleMatches(term);

  useEffect(() => {
    if (!orgId) return;
    const controller = new AbortController();
    setNodes(null);
    setFailed(false);

    void foldersApi
      .searchNodes(orgId, term, controller.signal)
      .then((found) => { if (!controller.signal.aborted) setNodes(found); })
      .catch(() => {
        if (!controller.signal.aborted) {
          setNodes([]);
          setFailed(true);
        }
      });

    return () => controller.abort();
  }, [orgId, term]);

  if (nodes === null) return <p className="text-sm text-muted">{t('common.loading')}</p>;

  const folders = nodes.filter((node) => node.kind === 'folder');
  const files = nodes.filter((node) => node.kind === 'document');
  const empty = apps.length === 0 && nodes.length === 0;

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <h1 className="text-lg font-semibold text-fg">{t('home.search.results', { term })}</h1>

      {failed && (
        <p role="alert" className="rounded-token border border-danger p-3 text-sm text-danger">
          {t('home.error.search')}
        </p>
      )}

      {empty && !failed && (
        <div className="rounded-token border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted">{t('home.search.noResults', { term })}</p>
        </div>
      )}

      {apps.length > 0 && (
        <HomeSection title={t('home.apps')}>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {apps.map((app) => (
              <li key={app.id}>
                <Link
                  to={`/${app.path}`}
                  className="flex items-center gap-3 rounded-token border border-border bg-surface-2 p-3 transition-colors hover:border-accent hover:bg-surface-3"
                >
                  <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-token ${app.chipClass}`}>
                    <Icon name={app.icon} />
                  </span>
                  <span className="min-w-0 break-words text-sm font-medium text-fg">{app.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </HomeSection>
      )}

      {folders.length > 0 && (
        <HomeSection title={t('folders.folders')}>
          <HomeCardGrid>
            {folders.map((node) => (
              <li key={node.id}>
                <NodeCard
                  node={node}
                  actions={actionsFor(node)}
                  starred={starredIds.has(node.id)}
                  onToggleStar={() => onToggleStar(node)}
                />
              </li>
            ))}
          </HomeCardGrid>
        </HomeSection>
      )}

      {files.length > 0 && (
        <HomeSection title={t('folders.files')}>
          <HomeCardGrid>
            {files.map((node) => (
              <li key={node.id}>
                <NodeCard
                  node={node}
                  actions={actionsFor(node)}
                  starred={starredIds.has(node.id)}
                  onToggleStar={() => onToggleStar(node)}
                />
              </li>
            ))}
          </HomeCardGrid>
        </HomeSection>
      )}
    </div>
  );
}
